import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classNames from 'classnames';

import {temporaryHtmlToString} from 'nutshell-core/list-items/temporary-html-to-string';

import {AccountAvatar} from '../avatar/account-avatar';
import {Avatar} from '../avatar/avatar';
import {colors} from '../colors/colors';
import {LinkedEntity} from '../linked-entity/linked-entity';
import {LoadingCircle} from '../loading-placeholder/loading-circle';
import {LoadingText} from '../loading-placeholder/loading-text';

import styles from './peep-two-line.module.css';

type Props = {
    name: string;
    avatarUrl: string | null | undefined;
    initials: string | null | undefined;
    htmlUrl?: string;
    type: string | null | undefined;
    relatedEntityName: string | null | undefined;
    isLinkable?: boolean;
    isLoading?: boolean;
    shouldClientRoute?: boolean;
    shouldCenterAlignSingleRow?: boolean;
    rightContent?: React.ReactNode | null | undefined;
    size?: 'mini' | 'normal';
};

export function PeepTwoLine(props: Props) {
    const {size = 'normal'} = props;

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    if (props.isLoading) {
        return (
            <div className={styles['peep-two-line']}>
                <div className={styles.avatar}>
                    <LoadingCircle size={32} color={colors.offWhiteDk} />
                </div>
                <div className={styles['peep-info']}>
                    <div className={styles['top-row']}>
                        <LoadingText height={13} color={colors.offWhiteDk} />
                    </div>
                    <div className={styles['bottom-row']}>
                        <LoadingText height={13} color={colors.offWhiteMd} />
                    </div>
                </div>
            </div>
        );
    }

    const twoLineStyleNames = classNames(styles['peep-two-line'], {
        [styles['peep-two-line--mini']]: size === 'mini',
    });

    const sanitizedName = temporaryHtmlToString(props.name) || props.name;
    const relatedEntityType = props.type === 'contacts' ? 'accounts' : 'contacts';

    // Loading state has no type, just satisfying flow
    if (!props.type) {
        return null;
    }

    return (
        <div className={twoLineStyleNames}>
            <div className={styles.avatar}>
                {props.type === 'accounts' ? (
                    <AccountAvatar avatarUrl={props.avatarUrl} />
                ) : (
                    <Avatar
                        type={props.type}
                        initials={props.initials}
                        avatarUrl={props.avatarUrl}
                        size={size === 'mini' ? 24 : 32}
                    />
                )}
            </div>
            <div className={styles['peep-info']}>
                <div
                    className={
                        props.shouldCenterAlignSingleRow
                            ? styles['top-row--align-single']
                            : styles['top-row']
                    }
                >
                    <LinkedEntity
                        name={
                            sanitizedName ? (
                                <span
                                    style={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {sanitizedName}
                                </span>
                            ) : undefined
                        }
                        htmlUrl={props.htmlUrl}
                        isLinkable={props.isLinkable}
                        shouldClientRoute={props.shouldClientRoute}
                        stopPropagation={true}
                        type={props.type}
                    />
                    {props.rightContent}
                </div>
                {props.relatedEntityName ? (
                    <div className={styles['bottom-row']}>
                        <div className={styles.associated}>
                            <LinkedEntity
                                name={props.relatedEntityName}
                                type={relatedEntityType}
                                isLinkable={false}
                                useEntityColor={true}
                            />
                        </div>
                    </div>
                ) : undefined}
            </div>
        </div>
    );
}
